import { useEffect, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlignLeft,
  Award,
  Building2,
  CalendarDays,
  ChevronDown,
  ClipboardList,
  FileText,
  Loader2,
  PencilLine,
  Scale,
  StickyNote,
  Zap,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { api } from "convex/_generated/api";
import { SideSheet } from "@/components/ui";
import { DatePicker } from "@/components/ui";
import { Dropdown } from "@/components/ui";
import { Button, Input, Textarea } from "@/components/ui/primitives";
import { useApiMutation } from "@/packages/features-shared";
import { TRIGGER_OPTIONS } from "./NewAuditWizard/wizardData";
import { AUDIT_STATUS_CONFIG } from "./constants";
import type { UIAudit } from "@/packages/types";
import { useTranslation } from "@/packages/utils";

// ─── Props ──────────────────────────────────────────────────────────────────

interface AuditEditSheetProps {
  audit: UIAudit;
  open: boolean;
  onClose: () => void;
}

// ─── Form State ─────────────────────────────────────────────────────────────

interface EditForm {
  displayName: string;
  deadline: number;
  description: string;
  trigger: string;
  notes: string;
  auditorRequestText: string;
}

function buildForm(audit: UIAudit): EditForm {
  return {
    displayName: audit.displayName,
    deadline: audit.deadline,
    description: audit.description ?? "",
    trigger: audit.trigger ?? "",
    notes: audit.notes ?? "",
    auditorRequestText: audit.auditorRequestText ?? "",
  };
}

// ─── Animation Variants ──────────────────────────────────────────────────────

const fieldVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.22, ease: "easeOut" as const },
  }),
};

// ─── Trigger Icons ────────────────────────────────────────────────────────────

const TRIGGER_ICONS: Record<string, LucideIcon> = {
  Regulatory: Scale,
  Certification: Award,
  Internal: Building2,
  "Ad-hoc": Zap,
};

// ─── Field Label ─────────────────────────────────────────────────────────────

function FieldLabel({
  children,
  required,
  icon: Icon,
}: {
  children: string;
  required?: boolean;
  icon?: LucideIcon;
}) {
  return (
    <div className="mb-1.5 flex items-center gap-1.5">
      {Icon && <Icon className="h-3.5 w-3.5 text-muted" />}
      <span className="text-[12px] font-medium text-secondary">
        {children}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </span>
    </div>
  );
}

// ─── Section Divider ─────────────────────────────────────────────────────────

function SectionDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 py-1">
      <div className="h-px flex-1 bg-border" />
      <span className="text-[10px] font-semibold tracking-widest uppercase text-muted">
        {label}
      </span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

// ─── Animated Error ───────────────────────────────────────────────────────────

function AnimatedError({ message }: { message?: string }) {
  return (
    <AnimatePresence>
      {message && (
        <motion.p
          key="error"
          initial={{ opacity: 0, height: 0, marginTop: 0 }}
          animate={{ opacity: 1, height: "auto", marginTop: 4 }}
          exit={{ opacity: 0, height: 0, marginTop: 0 }}
          transition={{ duration: 0.15 }}
          className="overflow-hidden text-[12px] text-destructive"
        >
          {message}
        </motion.p>
      )}
    </AnimatePresence>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export function AuditEditSheet({ audit, open, onClose }: AuditEditSheetProps) {
  const { t } = useTranslation(["audits", "common"]);
  const [form, setForm] = useState<EditForm>(() => buildForm(audit));
  const [errors, setErrors] = useState<Record<string, string>>({});

  const updateAudit = useApiMutation(api.audit_mutations.updateAudit, {
    name: "update-audit",
    successMessage: t("audits:edit.success"),
  });

  // Re-sync form when sheet opens
  useEffect(() => {
    if (open) {
      setForm(buildForm(audit));
      setErrors({});
    }
  }, [open, audit]);

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.displayName.trim())
      errs.displayName = t("audits:edit.validation.nameRequired");
    if (!form.deadline)
      errs.deadline = t("audits:edit.validation.dueDateRequired");
    if (!form.trigger)
      errs.trigger = t("audits:edit.validation.triggerRequired");
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    await updateAudit.mutateAsync({
      auditId: audit.id as Parameters<
        typeof updateAudit.mutateAsync
      >[0]["auditId"],
      displayName: form.displayName,
      deadline: form.deadline,
      description: form.description || undefined,
      trigger: form.trigger || undefined,
      notes: form.notes || undefined,
      auditorRequestText: form.auditorRequestText || undefined,
    });
    onClose();
  };

  const deadlineDate = form.deadline ? new Date(form.deadline) : undefined;

  const handleDeadlineChange = (date: Date | undefined) => {
    setForm((prev) => ({ ...prev, deadline: date ? date.getTime() : 0 }));
    if (date) setErrors((prev) => ({ ...prev, deadline: "" }));
  };

  const statusConfig = AUDIT_STATUS_CONFIG[audit.status];

  return (
    <SideSheet open={open} onClose={onClose}>
      <SideSheet.Header
        title={t("audits:edit.title")}
        description={audit.code}
        icon={<PencilLine size={18} />}
        tags={[
          {
            label: statusConfig.label,
            variant: "status",
            bgColor: statusConfig.badgeBg,
            color: statusConfig.badgeText,
          },
        ]}
      />

      <SideSheet.Body>
        <div className="space-y-5 py-2">
          {/* Name — full width */}
          <motion.div
            variants={fieldVariants}
            initial="hidden"
            animate="visible"
            custom={0}
          >
            <FieldLabel required icon={FileText}>
              {t("audits:edit.fields.name")}
            </FieldLabel>
            <Input
              placeholder={t("audits:edit.fields.namePlaceholder")}
              value={form.displayName}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, displayName: e.target.value }));
                if (e.target.value.trim())
                  setErrors((prev) => ({ ...prev, displayName: "" }));
              }}
            />
            <AnimatedError message={errors.displayName} />
          </motion.div>

          {/* Due Date + Trigger — 2-col */}
          <motion.div
            className="grid grid-cols-2 gap-4"
            variants={fieldVariants}
            initial="hidden"
            animate="visible"
            custom={1}
          >
            {/* Due Date */}
            <div>
              <FieldLabel required icon={CalendarDays}>
                {t("audits:edit.fields.dueDate")}
              </FieldLabel>
              <DatePicker
                value={deadlineDate}
                onChange={handleDeadlineChange}
                minDate={new Date()}
              >
                <DatePicker.Trigger variant="input" className="w-full" />
                <Popover.Portal>
                  <Popover.Content
                    side="bottom"
                    align="start"
                    sideOffset={4}
                    className="z-[60] rounded-[12px] border border-border bg-card shadow-lg"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                  >
                    <div className="flex">
                      <DatePicker.Presets />
                      <DatePicker.Calendar />
                    </div>
                    <DatePicker.Actions />
                  </Popover.Content>
                </Popover.Portal>
              </DatePicker>
              <AnimatedError message={errors.deadline} />
            </div>

            {/* Trigger */}
            <div>
              <FieldLabel required icon={Zap}>
                {t("audits:edit.fields.trigger")}
              </FieldLabel>
              <Dropdown>
                <Dropdown.Trigger>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between rounded-button border border-border bg-card px-3 py-2 text-[14px] text-left cursor-pointer"
                  >
                    <span
                      className={form.trigger ? "text-primary" : "text-muted"}
                    >
                      {form.trigger ||
                        t("audits:edit.fields.triggerPlaceholder")}
                    </span>
                    <ChevronDown className="h-4 w-4 text-muted" />
                  </button>
                </Dropdown.Trigger>
                <Dropdown.Content side="bottom" align="start">
                  <Dropdown.Label>
                    {t("audits:edit.fields.trigger")}
                  </Dropdown.Label>
                  {TRIGGER_OPTIONS.map((trigger) => {
                    const Icon = TRIGGER_ICONS[trigger];
                    return (
                      <Dropdown.Item
                        key={trigger}
                        onSelect={() => {
                          setForm((prev) => ({ ...prev, trigger }));
                          setErrors((prev) => ({ ...prev, trigger: "" }));
                        }}
                      >
                        <div className="flex items-center gap-2">
                          {Icon && <Icon className="h-3.5 w-3.5 text-muted" />}
                          {trigger}
                        </div>
                      </Dropdown.Item>
                    );
                  })}
                </Dropdown.Content>
              </Dropdown>
              <AnimatedError message={errors.trigger} />
            </div>
          </motion.div>

          {/* Section divider */}
          <SectionDivider label={t("audits:edit.sections.additionalDetails")} />

          {/* Description */}
          <motion.div
            variants={fieldVariants}
            initial="hidden"
            animate="visible"
            custom={2}
          >
            <FieldLabel icon={AlignLeft}>
              {t("audits:edit.fields.description")}
            </FieldLabel>
            <Textarea
              placeholder={t("audits:edit.fields.descriptionPlaceholder")}
              rows={5}
              value={form.description}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, description: e.target.value }))
              }
            />
          </motion.div>

          {/* Notes */}
          <motion.div
            variants={fieldVariants}
            initial="hidden"
            animate="visible"
            custom={3}
          >
            <FieldLabel icon={StickyNote}>
              {t("audits:edit.fields.notes")}
            </FieldLabel>
            <Textarea
              placeholder={t("audits:edit.fields.notesPlaceholder")}
              rows={4}
              value={form.notes}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, notes: e.target.value }))
              }
            />
          </motion.div>

          {/* Auditor Request Text */}
          <motion.div
            variants={fieldVariants}
            initial="hidden"
            animate="visible"
            custom={4}
          >
            <FieldLabel icon={ClipboardList}>
              {t("audits:edit.fields.auditorInstructions")}
            </FieldLabel>
            <Textarea
              placeholder={t(
                "audits:edit.fields.auditorInstructionsPlaceholder",
              )}
              rows={3}
              value={form.auditorRequestText}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  auditorRequestText: e.target.value,
                }))
              }
            />
          </motion.div>
        </div>
      </SideSheet.Body>

      <SideSheet.Footer>
        <Button variant="secondary" onClick={onClose}>
          {t("common:buttons.cancel")}
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={updateAudit.isPending}
        >
          {updateAudit.isPending ? (
            <span className="flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {t("audits:edit.actions.saving")}
            </span>
          ) : (
            t("audits:edit.actions.saveChanges")
          )}
        </Button>
      </SideSheet.Footer>
    </SideSheet>
  );
}
